import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { SearchFilterState } from '../searchState';
import FilterPanel from './FilterPanel';
import './MobileFilterSheet.css';

interface MobileFilterSheetProps {
  state: SearchFilterState;
  onChange: (patch: Partial<SearchFilterState>) => void;
  onClearAll: () => void;
  onClose: () => void;
  totalCount?: number;
  /**
   * Scroll this filter group into view on open. Set by the arrival coachmark's
   * "Show me the filter", which promises to land the user on a specific group
   * rather than merely opening the sheet.
   */
  scrollToGroupId?: string;
}

export const MobileFilterSheet: React.FC<MobileFilterSheetProps> = ({
  state,
  onChange,
  onClearAll,
  onClose,
  totalCount,
  scrollToGroupId,
}) => {
  /**
   * The Trade group happens to be the first section today, so at scroll 0 it is
   * already in view — but that is an accident of section order, not a promise.
   * Scrolling explicitly keeps "Show me the filter" honest if a group is ever
   * inserted above it (e.g. when "Available to send" comes back).
   *
   * `block: 'start'` inside .mobile-sheet-body, which is the scroll container.
   */
  useEffect(() => {
    if (!scrollToGroupId) return;
    const target = document.getElementById(scrollToGroupId);
    if (!target) return;
    target.scrollIntoView({ block: 'start', behavior: 'auto' });
  }, [scrollToGroupId]);

  // The page behind the sheet must not scroll with it.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  // The count is for the filters as they stand, so the button always says what
  // dismissing the sheet will show. Undefined only before the first response.
  const applyLabel =
    totalCount === undefined
      ? 'Show results'
      : `Show ${totalCount.toLocaleString()} ${totalCount === 1 ? 'card' : 'cards'}`;

  // Portalled to the body: .main-content-wrapper has overflow:hidden and would
  // otherwise clip the sheet.
  return createPortal(
    <>
      <div className="mobile-sheet-overlay" onClick={onClose} aria-hidden="true" />
      <div className="mobile-filter-sheet" role="dialog" aria-modal="true" aria-label="Filters">
        <button type="button" className="mobile-sheet-handle" onClick={onClose} aria-label="Close filters">
          <span className="mobile-sheet-handle-bar" />
        </button>

        <div className="mobile-sheet-body">
          <FilterPanel state={state} onChange={onChange} onClearAll={onClearAll} touch />
        </div>

        <div className="mobile-sheet-footer">
          <button type="button" className="mobile-sheet-apply" onClick={onClose}>
            {applyLabel}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default MobileFilterSheet;
